import { queryOptions, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { api } from '@/lib/api-client';
import { QueryConfig } from '@/lib/react-query';

import { SessionData } from '../types';

export const getSession = (): Promise<SessionData> => {
  return api.get('/session', { withCredentials: true });
};

export const getSessionQueryOptions = () => {
  return queryOptions({
    queryKey: ['session'],
    queryFn: getSession,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

type UseSessionOptions = {
  queryConfig?: QueryConfig<typeof getSessionQueryOptions>;
};

// Custom type guard: Asserts session is defined if isLoggedIn is true
const isSessionDefined = (
  queryData: SessionData | undefined,
  isLoggedIn: boolean,
): queryData is SessionData & { user: NonNullable<SessionData['user']> } => {
  return isLoggedIn && !!queryData?.user?.username;
};

export const useSession = ({ queryConfig }: UseSessionOptions = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery({
    ...getSessionQueryOptions(),
    ...queryConfig,
  });

  useEffect(() => {
    if (query.data) {
      const { isRegistered, isLoggedIn } = query.data;

      // List of routes that should bypass the redirect
      const publicRoutes = ['/terms', '/privacy', '/register'];

      if (
        !isRegistered &&
        isLoggedIn && // User not registered
        !publicRoutes.includes(location.pathname) // Exclude public routes
      ) {
        // Redirect to registration page
        toast.warning('Please complete your registration to continue', {
          id: 'registration-required',
        });
        navigate(`/register`);
      }
    }
  }, [query.data, navigate, location.pathname]);

  const isLoggedIn = !!query.data?.isLoggedIn;

  if (isSessionDefined(query.data, isLoggedIn)) {
    return {
      ...query,
      isLoggedIn: true,
      session: query.data,
      hasSteamId: !!query.data.user.steamid,
    } as const;
  }

  return {
    ...query,
    isLoggedIn: false,
    session: undefined,
    hasSteamId: false,
  } as const;
};
